import { computed, observable, observe, runInAction } from "mobx"
import { Observable, type Subscription } from "rxjs"

import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { type IRemoteLogModel } from "@/routes/tasks/TaskViewScene/RemoteLogModel.ts"
import { TerminalModel } from "@/routes/tasks/TaskViewScene/TerminalModel.ts"

import type { ExecCommandResult, TID } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Queries } from "@/lib/queries.ts"

const log = getLogger("ExecResultTerminalModel")

export class ExecResultTerminalModel extends Model implements IRemoteLogModel {
  private queries: Queries
  private readonly taskId: string
  private readonly execResultId: string
  private log = observable.box<ExecCommandResult | null>(null)
  private hasUpdates = false
  private updating = false
  readonly available = observable.box(false)
  readonly id: string
  readonly label: string
  readonly commandId?: string
  readonly previewId?: string
  terminalModel: TerminalModel

  constructor(params: {
    taskId: string,
    execResultId: string,
    queries: Queries,
    commandId?: string,
    previewId?: string
  }) {
    super("ExecResultTerminalModel")
    this.queries = params.queries
    this.taskId = params.taskId
    this.execResultId = params.execResultId
    this.id = params.execResultId
    this.label = params.execResultId
    this.commandId = params.commandId
    this.previewId = params.previewId
    this.terminalModel = new TerminalModel(this.lifetime, null, this)

    void this.subscribe()
  }

  getLogs: () => Observable<string> = () => {
    return new Observable((subscriber) => {
      const createFireFn = () => {
        let prevOutputLength = 0
        return (output: string | null | undefined) => {
          const prevLength = prevOutputLength
          const nextLength = output?.length ?? 0
          if (nextLength > prevLength) {
            subscriber.next(output?.substring(prevLength) ?? "")
            prevOutputLength = nextLength
          }
        }
      }

      const stdoutFire = createFireFn()
      const stderrFire = createFireFn()

      const disposer = observe(this.log, (change) => {
        stdoutFire(change.newValue?.stdout)
        stderrFire(change.newValue?.stderr)
      }, true)

      let stdoutFromEnvDisposer: Subscription | null = null
      if (this.commandId && this.previewId) {
        // TODO: support multiple previews in Matter-1205 Storybook 📚
        void this.queries.apiClient.subscribeToPreviewAppLogs(this.previewId! as TID, null, this.commandId!).then((it) => {
          let stdoutFromEnv = ""
          stdoutFromEnvDisposer = it.subscribe((value) => {
            stdoutFromEnv += value
            stdoutFire(stdoutFromEnv)
          })
        })
      }

      return () => {
        disposer()
        stdoutFromEnvDisposer?.unsubscribe()
      }
    })
  }

  private subscribe = async () => {
    const updates = this.queries.commandOutputUpdates(this.taskId)

    this.lifetime.add(updates.subscribe(() => {
      this.hasUpdates = true
      void this.updateIfNeeded()
    }), "updates")

    this.hasUpdates = true
    await this.updateIfNeeded()
  }

  private updateIfNeeded = async () => {
    if (this.updating) {
      return
    }
    this.updating = true
    try {
      while (this.hasUpdates) {
        this.hasUpdates = false
        try {
          const currentLog = this.log.get()
          const nextLog = await this.queries.getExecCommandResult(
            this.execResultId as TID,
            currentLog?.stdout?.length ?? 0,
            currentLog?.stderr?.length ?? 0,
          )
          runInAction(() => {
            this.log.set({
              ...nextLog,
              stdout: (currentLog?.stdout ?? "") + nextLog.stdout,
              stderr: (currentLog?.stderr ?? "") + nextLog.stderr
            })
          })
        } catch (ex) {
          log.error(`Failed to update exec command result ${this.execResultId}`, ex)
        }
        runInAction(() => {
          this.available.set(true)
        })
      }
    } finally {
      this.updating = false
    }
  }
}

export class ExecResultFullReloadModel extends Model {
  private queries: Queries
  private readonly taskId: string
  private readonly execResultId: string
  private log = observable.box<ExecCommandResult | null>(null)
  private hasUpdates = false
  private updating = false
  stdout = computed(() => this.log.get()?.stdout ?? "")

  constructor(params: {
    taskId: string,
    execResultId: string,
    queries: Queries
  }) {
    super("ExecResultFullReloadModel")
    this.queries = params.queries
    this.taskId = params.taskId
    this.execResultId = params.execResultId
    void this.subscribe()
  }

  subscribe = async () => {
    const updates = this.queries.commandOutputUpdates(this.taskId)

    this.lifetime.add(updates.subscribe(() => {
      this.hasUpdates = true
      void this.updateIfNeeded()
    }), "updates")

    this.hasUpdates = true
    await this.updateIfNeeded()
  }

  updateIfNeeded = async () => {
    if (this.updating) {
      return
    }
    this.updating = true
    try {
      while (this.hasUpdates) {
        this.hasUpdates = false
        try {
          const nextLog = await this.queries.getExecCommandResult(this.execResultId as TID, 0, 0)
          runInAction(() => {
            this.log.set(nextLog)
          })
        } catch (ex) {
          log.error(`Failed to update exec command result ${this.execResultId}`, ex)
        }
      }
    } finally {
      this.updating = false
    }
  }
}